// Browser and app information utility functions

export interface BrowserInfo {
  userAgent: string;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: string;
  screenResolution: string;
  viewportSize: string;
  language: string;
  timezone: string;
  cookiesEnabled: boolean;
  localStorageEnabled: boolean;
  sessionStorageEnabled: boolean;
  online: boolean;
  connectionType?: string;
  connectionSpeed?: string;
}

export interface AppInfo {
  appName: string;
  appVersion: string;
  environment: string;
  buildTime?: string;
  currentUrl: string;
  referrer: string;
  pageLoadTime?: number;
}

export function getBrowserInfo(): BrowserInfo {
  const userAgent = navigator.userAgent;
  
  // Browser detection
  let browser = 'Unknown';
  let browserVersion = 'Unknown';
  
  if (userAgent.includes('Chrome')) {
    browser = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Edge')) {
    browser = 'Edge';
    const match = userAgent.match(/Edge\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) {
    browser = 'Internet Explorer';
    const match = userAgent.match(/MSIE (\d+)/) || userAgent.match(/rv:(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }

  // OS detection
  let os = 'Unknown';
  let osVersion = 'Unknown';
  
  if (userAgent.includes('Windows')) {
    os = 'Windows';
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    osVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Mac')) {
    os = 'macOS';
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'Unknown';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
    const match = userAgent.match(/Linux[^)]*\)/);
    osVersion = match ? match[0] : 'Unknown';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
    const match = userAgent.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('iOS')) {
    os = 'iOS';
    const match = userAgent.match(/OS (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'Unknown';
  }

  // Device detection
  let device = 'Desktop';
  if (userAgent.includes('Mobile')) {
    device = 'Mobile';
  } else if (userAgent.includes('Tablet')) {
    device = 'Tablet';
  }

  // Connection information
  let connectionType: string | undefined;
  let connectionSpeed: string | undefined;
  
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    connectionType = connection?.effectiveType || connection?.type;
    connectionSpeed = connection?.downlink ? `${connection.downlink} Mbps` : undefined;
  }

  return {
    userAgent,
    browser,
    browserVersion,
    os,
    osVersion,
    device,
    screenResolution: `${screen.width}x${screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookiesEnabled: navigator.cookieEnabled,
    localStorageEnabled: typeof Storage !== 'undefined',
    sessionStorageEnabled: typeof sessionStorage !== 'undefined',
    online: navigator.onLine,
    connectionType,
    connectionSpeed
  };
}

export function getAppInfo(): AppInfo {
  return {
    appName: 'CareAscendium',
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
    environment: process.env.NODE_ENV || 'development',
    buildTime: process.env.NEXT_PUBLIC_BUILD_TIME,
    currentUrl: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    pageLoadTime: typeof performance !== 'undefined' ? performance.now() : undefined
  };
}

export function getCombinedInfo() {
  return {
    browser: getBrowserInfo(),
    app: getAppInfo()
  };
} 